import React, { useCallback, useMemo } from 'react';
import { useWatch, Controller } from 'react-hook-form';
import { Box, TextField, IconButton, Divider } from '@mui/material';
import { Add } from '@mui/icons-material';
import type { AuditDynamicFormTicketRowProps } from '../../types/auditFormTypes';
import { baseOutlinedInputStyles, buildOutlinedErrorBorderStyles, mergeOutlinedInputStyles } from './AuditDynamicFormConstants';
import AuditDynamicFormFieldIcon from './AuditDynamicFormFieldIcon';
import AuditDynamicFormSoldCalculation from './AuditDynamicFormSoldCalculation';
import AuditDynamicFormVarianceCountCell from './AuditDynamicFormVarianceCountCell';
import AuditDynamicFormVarianceStatusIconCell from './AuditDynamicFormVarianceStatusIconCell';

const AuditDynamicFormTicketRow: React.FC<AuditDynamicFormTicketRowProps> = React.memo(({
  field,
  idx,
  ticket,
  control,
  rowErrors,
  setValue,
  remove: onRemove,
  onInsert,
  varianceByGameId,
  hoveredRowIndex,
  onRowHover,
}) => {
  const gamePackStyle = baseOutlinedInputStyles;
  const rangeStyle = baseOutlinedInputStyles;
  const isMissingGamePack = ticket?.isMissing;
  const isEditable = true;
  const hasNameError = !!rowErrors?.name;
  const hasGamePackError = !!rowErrors?.game || !!rowErrors?.pack;
  const hasRangeError = !!rowErrors?.range;

  const handleRemove = useCallback(() => {
    onRemove(idx);
  }, [idx, onRemove]);

  const watchedGame = useWatch({
    control,
    name: 'ticket_details.' + idx + '.game',
  });

  const watchedPack = useWatch({
    control,
    name: 'ticket_details.' + idx + '.pack',
  });

  const gamePackValue = useMemo(() => {
    if (watchedGame && watchedPack) {
      return String(watchedGame) + '-' + String(watchedPack);
    }

    if (watchedGame) {
      return String(watchedGame);
    }

    return '';
  }, [watchedGame, watchedPack]);

  const variance = useMemo(() => {
    const gameIdKey = watchedGame != null && String(watchedGame).trim() ? String(watchedGame).trim() : String(ticket?.game || '').trim();
    if (!gameIdKey) {
      return undefined;
    }

    return varianceByGameId?.[gameIdKey];
  }, [ticket?.game, varianceByGameId, watchedGame]);

  const updateGamePack = useCallback((value: string) => {
    const cleanVal = value.replace(/[^\d-]/g, '');
    const parts = cleanVal.split('-');
    const gameFieldName = 'ticket_details.' + idx + '.game';
    const packFieldName = 'ticket_details.' + idx + '.pack';

    if (parts.length >= 2) {
      setValue(gameFieldName, parts[0], { shouldDirty: true, shouldTouch: true });
      setValue(packFieldName, parts[1], { shouldDirty: true, shouldTouch: true });
      return;
    }

    setValue(gameFieldName, cleanVal, { shouldDirty: true, shouldTouch: true });
    setValue(packFieldName, '', { shouldDirty: true, shouldTouch: true });
  }, [idx, setValue]);

  const isHovered = hoveredRowIndex === idx;
  const isRowHovered = hoveredRowIndex === idx + 1;
  const highlightColor = '#000000';

  return (
    <React.Fragment key={`ocr-form-field-${field.id}-${idx}`}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: '#fbfafe',
          cursor: 'pointer',
          opacity: 1,
          '&:hover .add-btn': { opacity: 1 },
        }}
      >
        <Box
          className="add-btn"
          sx={{
            width: 'calc( 100% - 20px )',
            height: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <IconButton size="small" color="primary" sx={{ p: 0, width: 20, height: 20 }} onClick={() => onInsert(idx)}>
            <Add sx={{ fontSize: 20 }} />
          </IconButton>
          <Divider
            sx={{
              borderColor: isHovered ? highlightColor : '#A3AED099',
              borderWidth: '0.5px',
              borderStyle: 'solid',
              width: '100%',
              transition: 'all 0.1s ease-in-out',
              boxShadow: isHovered ? `0 0 8px ${highlightColor}40` : 'none',
            }}
          />
        </Box>
      </Box>

      <Box
        onMouseEnter={() => onRowHover?.(idx + 1)}
        onMouseLeave={() => onRowHover?.(null)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 0,
          py: 0,
          height: '17px',
          minHeight: '17px',
          maxHeight: '17px',
          bgcolor: isMissingGamePack ? '#f8f9fa' : '#fcfcfc',
          position: 'relative',
          '&:hover .minus-btn': { opacity: 1 },
          gap: 0.25,
        }}
      >
        <Controller
          name={`ticket_details.${idx}.name`}
          control={control}
          render={({ field: nameField }) => (
            <Box
              sx={{
                maxWidth: '140px',
                minWidth: '120px',
                flexBasis: '15%',
                textAlign: 'left',
                px: 0.5,
                py: 0,
                ml: 2.5,
                height: '17px',
                maxHeight: '17px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <TextField
                disabled={ticket?.newField === true ? false : true}
                {...nameField}
                placeholder="Name"
                error={!!rowErrors?.name}
                size="small"
                sx={mergeOutlinedInputStyles({
                  ...buildOutlinedErrorBorderStyles(hasNameError),
                  width: '100%',
                  minWidth: '120px',
                  maxWidth: '140px',
                  color: '#222B45',
                  '& .MuiInputBase-input': {
                    py: '2px',
                    px: '4px',
                    fontSize: 15,
                    fontWeight: 500,
                    color: '#222B45',
                    height: '17px',
                    minHeight: '17px',
                    maxHeight: '17px',
                    lineHeight: '17px',
                  },
                })}
                InputLabelProps={{ shrink: false }}
              />
            </Box>
          )}
        />

        <Controller
          name={`ticket_details.${idx}.game`}
          control={control}
          render={({ field: gameField }) => (
            <Box
              sx={{
                flexBasis: '20%',
                maxWidth: '140px',
                minWidth: '120px',
                textAlign: 'left',
                px: 0.5,
                py: 0,
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                height: '17px',
                maxHeight: '17px',
                marginRight: '24px',
              }}
            >
              <TextField
                value={gamePackValue}
                onChange={(event) => {
                  if (!isEditable) {
                    return;
                  }

                  let nextValue = event.target.value;

                  if (nextValue.length > 4 && !nextValue.includes('-')) {
                    const part1 = nextValue.slice(0, 4);
                    const part2 = nextValue.slice(4);
                    nextValue = `${part1}-${part2}`;
                  }

                  updateGamePack(nextValue);
                }}
                onBlur={gameField.onBlur}
                name={gameField.name}
                placeholder="1234-1234567"
                error={!!rowErrors?.game || !!rowErrors?.pack}
                size="small"
                disabled={!isEditable}
                sx={{
                  ...gamePackStyle,
                  ...buildOutlinedErrorBorderStyles(hasGamePackError),
                  width: '100%',
                  minWidth: '120px',
                  maxWidth: '140px',
                  color: '#222B45',
                  '& .MuiInputBase-input': {
                    ...gamePackStyle['& .MuiInputBase-input'],
                    py: '2px',
                    px: '4px',
                    fontSize: 15,
                    fontWeight: 500,
                    color: '#222B45',
                    height: '17px',
                    minHeight: '17px',
                    maxHeight: '17px',
                    lineHeight: '17px',
                  },
                }}
                inputProps={{ maxLength: 12 }}
                InputLabelProps={{ shrink: false }}
              />

              <Box
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pointerEvents: 'none',
                  height: '17px',
                  maxHeight: '17px',
                }}
              >
                <Box sx={{ height: '8px', maxHeight: '8px', display: 'flex', alignItems: 'center', pointerEvents: 'auto' }}>
                  <AuditDynamicFormFieldIcon ticket={ticket} fieldType="pack" />
                </Box>
              </Box>
            </Box>
          )}
        />

        <Controller
          name={`ticket_details.${idx}.range`}
          control={control}
          render={({ field: rangeField }) => {
            let [rangeStart = '', rangeEnd = ''] = String(rangeField.value || '').split('-');
            const ticketsPerPackValue = ticket?.ticketsPerPack;
            const ticketsPerPackText = ticketsPerPackValue === undefined || ticketsPerPackValue === null
              ? ''
              : String(ticketsPerPackValue);

            if (rangeEnd === '' || rangeField.value === '') {
              rangeEnd = '';
            } else {
              rangeEnd = isNaN(parseInt(rangeEnd, 10))
                ? ticketsPerPackText
                  ? ticketsPerPackText.length < 3
                    ? '0'.repeat(3 - ticketsPerPackText.length) + ticketsPerPackText
                    : ticketsPerPackText
                  : rangeEnd
                : rangeEnd;
            }

            return (
              <Box
                sx={{
                  width: '20%',
                  flexBasis: '20%',
                  maxWidth: '140px',
                  minWidth: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  px: 0.5,
                  py: 0,
                  height: '17px',
                  maxHeight: '17px',
                }}
              >
                <Box sx={{ position: 'relative', height: '17px', maxHeight: '17px' }}>
                  <TextField
                    value={rangeStart}
                    onChange={(event) => {
                      if (!isEditable) {
                        return;
                      }

                      const newStart = event.target.value.replace(/[^\d]/g, '').slice(0, 3);
                      rangeField.onChange(`${newStart}-${rangeEnd}`);
                    }}
                    placeholder="Start"
                    size="small"
                    disabled={!isEditable}
                    error={hasRangeError}
                    sx={{
                      ...rangeStyle,
                      ...buildOutlinedErrorBorderStyles(hasRangeError),
                      width: '46px',
                      minWidth: '46px',
                      maxWidth: '46px',
                      color: '#222B45',
                      '& .MuiInputBase-input': {
                        ...rangeStyle['& .MuiInputBase-input'],
                        py: '2px !important',
                        px: '4px !important',
                        fontSize: 16,
                        fontWeight: 500,
                        color: '#222B45',
                        height: '17px',
                        minHeight: '17px',
                        maxHeight: '17px',
                        lineHeight: '17px',
                        maxWidth: '46px',
                        width: '46px',
                        padding: '4px',
                      },
                      '& .MuiOutlinedInput-input': {
                        ...rangeStyle['& .MuiOutlinedInput-input'],
                        p: '2px 4px !important',
                        textAlign: 'center',
                      },
                    }}
                    InputLabelProps={{ shrink: false }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -1,
                      pointerEvents: 'auto',
                      height: '8px',
                      maxHeight: '8px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <AuditDynamicFormFieldIcon ticket={ticket} fieldType="range" />
                  </Box>
                </Box>

                <Box sx={{ px: 0.25, fontWeight: 700, color: '#888', fontSize: 15, height: '17px', maxHeight: '17px', display: 'flex', alignItems: 'center' }}>
                  -
                </Box>

                <Box sx={{ position: 'relative', height: '17px', maxHeight: '17px' }}>
                  <TextField
                    value={rangeEnd}
                    onChange={(event) => {
                      if (!isEditable) {
                        return;
                      }

                      const newEnd = event.target.value.replace(/[^\dA-Z]/g, '').slice(0, 3);
                      rangeField.onChange(`${rangeStart}-${newEnd}`);
                    }}
                    placeholder="End"
                    size="small"
                    disabled={!isEditable}
                    error={hasRangeError}
                    sx={mergeOutlinedInputStyles({
                      ...rangeStyle,
                      ...buildOutlinedErrorBorderStyles(hasRangeError),
                      width: '46px',
                      minWidth: '46px',
                      maxWidth: '46px',
                      color: '#222B45',
                      '& .MuiInputBase-input': {
                        ...rangeStyle['& .MuiInputBase-input'],
                        py: '2px',
                        px: '4px',
                        fontSize: 16,
                        fontWeight: 500,
                        color: '#222B45',
                        height: '17px',
                        minHeight: '17px',
                        maxHeight: '17px',
                        lineHeight: '17px',
                        width: '46px',
                        padding: '4px',
                      },
                      '& .MuiOutlinedInput-input': {
                        ...rangeStyle['& .MuiOutlinedInput-input'],
                        p: '2px 4px !important',
                        textAlign: 'center',
                      },
                    })}
                    InputLabelProps={{ shrink: false }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      pointerEvents: 'auto',
                      height: '17px',
                      maxHeight: '17px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <AuditDynamicFormFieldIcon ticket={ticket} fieldType="range-end" />
                  </Box>
                </Box>
              </Box>
            );
          }}
        />

        <AuditDynamicFormSoldCalculation idx={idx} control={control} ticket={ticket} />

        <AuditDynamicFormVarianceCountCell variance={variance} />

        <AuditDynamicFormVarianceStatusIconCell variance={variance} />

        <Box
          sx={{
            minWidth: 36,
            width: 36,
            maxWidth: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
          }}
        >
          <IconButton
            className="minus-btn"
            onClick={handleRemove}
            size="small"
            sx={{
              opacity: isRowHovered ? 1 : 0,
              transition: 'opacity 0.2s',
              color: 'rgba(34, 43, 69, 1)',
              '&:hover': { opacity: 1 },
              '&.Mui-disabled': { opacity: 0.3 },
              padding: 0,
              width: '17px',
              height: '17px',
              minWidth: '17px',
              minHeight: '17px',
              maxWidth: '17px',
              maxHeight: '17px',
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 700, lineHeight: '17px', height: '17px' }}>
              -
            </span>
          </IconButton>
        </Box>
      </Box>
    </React.Fragment>
  );
}, (prevProps, nextProps) => {
  const prevIsHovered = prevProps.hoveredRowIndex === prevProps.idx;
  const nextIsHovered = nextProps.hoveredRowIndex === nextProps.idx;

  if (prevIsHovered !== nextIsHovered) {
    return false;
  }

  return !(
    prevProps.idx !== nextProps.idx ||
    prevProps.field.id !== nextProps.field.id ||
    prevProps.ticket !== nextProps.ticket ||
    prevProps.control !== nextProps.control ||
    prevProps.rowErrors !== nextProps.rowErrors ||
    prevProps.setValue !== nextProps.setValue ||
    prevProps.remove !== nextProps.remove ||
    prevProps.onInsert !== nextProps.onInsert ||
    prevProps.varianceByGameId !== nextProps.varianceByGameId ||
    prevProps.onRowHover !== nextProps.onRowHover
  );
});

AuditDynamicFormTicketRow.displayName = 'AuditDynamicFormTicketRow';

export default AuditDynamicFormTicketRow;
